// The up-front "which business unit?" step of a create flow. Sits above the
// choices it narrows (templates, job types) so the reason the list shrank is
// visible rather than mysterious.
//
// Self-gating: renders NOTHING when the tenant has no active business units, so
// a tenant that never adopted the concept sees the create flows exactly as they
// were.
//
// Two postures, chosen by the flow:
//   • optional (default) — leaving it empty applies no filter at all (it does
//     not mean "unscoped items only").
//   • required — the flow will not go on without an answer, and says so. While
//     the unit list is still in flight the card renders a placeholder instead
//     of vanishing, so the gate the caller holds closed is never a bare hint
//     with nothing to act on. Once the list settles on "no units" the card
//     disappears as before, and the caller's gate opens with it.
import { Building2 } from 'lucide-react';
import { Skeleton } from '@/components/atoms/skeleton';
import { BusinessUnitSelect } from './BusinessUnitSelect';
import { useBusinessUnitOptions } from './useBusinessUnitOptions';

export interface BusinessUnitScopeStepProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Names what the choice narrows, e.g. "templates" or "job types". */
  narrows?: string;
  /** The flow will not proceed without a unit; the copy and placeholder say so. */
  required?: boolean;
}

export function BusinessUnitScopeStep({
  value,
  onChange,
  narrows = 'options',
  required = false,
}: BusinessUnitScopeStepProps) {
  const { activeUnits, isLoaded, isError } = useBusinessUnitOptions();
  const pending = required && !isLoaded && !isError;
  if (activeUnits.length === 0 && !pending) return null;

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="flex items-center gap-3 px-4 pb-3 pt-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Building2 className="h-4 w-4 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-semibold">Business unit</p>
          <p className="text-xs text-muted-foreground">
            {required
              ? `Required — the unit this work belongs to. It decides which ${narrows} are offered below.`
              : `Optional — narrows the ${narrows} below. Leave it empty to see everything.`}
          </p>
        </div>
      </div>
      <div className="px-4 pb-4">
        {pending ? (
          <Skeleton className="h-9 w-full rounded-md" data-testid="business-unit-step-loading" />
        ) : (
          <BusinessUnitSelect
            value={value}
            onChange={onChange}
            placeholder={required ? 'Select a business unit' : 'All business units'}
          />
        )}
      </div>
    </div>
  );
}
